import * as React from "react"
import { useLayoutEffect, useMemo, useRef } from "react"
import { Box, LinearProgress, Skeleton, Typography } from "@mui/material"
import { TableSeeker, IconSeeker } from "@ui-seeker/core"

import {
    appliedFilters,
    columnDisplay,
    coverage,
    coverageTotal,
    currentPage,
    detailsRow,
    detailsTab,
    hasSearched,
    isLoadingTable,
    offerSummary,
    pageSize,
    searchText,
    selectedSites,
    sitesOptions,
    sortOrder,
    tableData,
    totalCount,
} from "../@signals"
import { buildColumns } from "./columns"
import ScrapingDataReviewQualityFilters from "../Filters/QualityFilters"


/**
 * The div mui-datatables puts the horizontal overflow on.
 *
 * Its class is hashed (`tss-<hash>-MUIDataTable-responsiveBase`), so match the
 * stable part, and fall back to whatever descendant actually scrolls in case a
 * future version renames it.
 */
function findHorizontalScroller(root) {
    if (!root) return null
    const byClass = root.querySelector('[class*="responsiveBase"]')
    if (byClass) return byClass
    return Array.from(root.querySelectorAll('div'))
        .find(node => node.scrollWidth > node.clientWidth + 1) || null
}


function restoreScroll(root, left) {
    const scroller = findHorizontalScroller(root)
    if (scroller) scroller.scrollLeft = left
}


//: From the APPLIED filters, not the live ones: the title describes the rows on
//: screen, and moving the date filter without pressing Search used to relabel
//: them as something they were not.
//: Where the drawer's Offers tab sits: Features, Description, Internal Info,
//: Offers.
const OFFERS_TAB = 3


function windowLabel(applied) {
    if (!applied?.recentDataEnabled) return 'all time'
    if (applied.recentDataFrom || applied.recentDataTo) return 'the selected dates'
    return `the last ${applied.recentDataDays} days`
}


export default function ScrapingDataReviewTable() {
    const wrapperRef = useRef(null)
    const scrollLeftRef = useRef(0)
    const data = tableData.value
    const total = totalCount.value
    const isLoading = isLoadingTable.value
    const coverageValues = coverage.value

    // Site labels come from the filter's own options: the payload carries
    // siteID, and re-fetching a name per row would be a query per row.
    //
    // ⚠️ From the whole catalogue, not only the chosen site. A market filter
    // narrows `sitesOptions`, and rows can still carry a site that is no longer
    // in that narrowed list; reading the selection alone once left every row
    // labelled `Site 491`. The catalogue is loaded on mount with `all=1`.
    const siteById = useMemo(() => {
        const map = {}
        for (const site of sitesOptions.value || []) map[site.value] = site
        for (const site of selectedSites.value || []) map[site.value] = site
        return map
    }, [sitesOptions.value, selectedSites.value])

    const tableRows = useMemo(() => data.map(item => ({
        ...item,
        search_text: [item.productID, item.brandRaw, item.productTitle, item.ean, item.reference]
            .filter(Boolean).join(' '),
        retailerInfo: {
            label: siteById[item.siteID]?.label || `Site ${item.siteID}`,
            countryCode: siteById[item.siteID]?.countryCode || '',
            hasDescription: item.hasDescription,
            richContent: !!item.richContent,
            inStock: item.stockText === 'stock',
            // Arrives after the table, in its own request. Undefined until it
            // lands, and absent for products with a single seller.
            offers: offerSummary.value[`${item.siteID}:${item.productID}`],
            sellerName: item.sellerName,
            sellerCode: item.sellerCode,
            row: item,
        },
        productRaw: {
            productID: item.productID,
            productTitle: item.productTitle,
            productUrl: item.productUrl,
            brandRaw: item.brandRaw,
            thumbnail: item.thumbnail,
            date: item.date,
            row: item,
        },
        // Each money column carries its own currency: the renderer only gets
        // the cell value, and the symbol belongs to the row.
        price: { value: item.actualPrice, currency: item.currency },
        listPrice: { value: item.listPrice, currency: item.currency },
        shippingPrice: { value: item.shippingPrice, currency: item.currency },
    })), [data, siteById, offerSummary.value])

    const columns = useMemo(
        () => buildColumns(
            coverageValues,
            (row) => { detailsTab.value = 0; detailsRow.value = row },
            columnDisplay.value,
            // The sellers chip opens the same drawer, straight on its Offers tab.
            (row) => { detailsTab.value = OFFERS_TAB; detailsRow.value = row },
        ),
        [coverageValues, columnDisplay.value]
    )

    // Safety net for a real remount (the column set changing rebuilds the
    // grid). The reload path no longer unmounts — see the dimming below — but
    // when the scroller IS rebuilt, put it back where it was.
    useLayoutEffect(() => {
        if (isLoading || !scrollLeftRef.current) return
        const left = scrollLeftRef.current
        restoreScroll(wrapperRef.current, left)
        // Column widths settle a frame later (thumbnails, chips), and a
        // scrollLeft set against a still-narrow table gets clamped. Ask twice.
        const frame = requestAnimationFrame(() => restoreScroll(wrapperRef.current, left))
        return () => cancelAnimationFrame(frame)
    }, [isLoading, data])

    // Only the very first load gets the skeleton. Swapping a full table for a
    // 500px block on every sort or page change collapsed the page height, so
    // the browser scrolled back to the top AND the grid's own horizontal
    // scroller was destroyed with it: sorting by a column on the right threw
    // away both positions. A reload now dims the table in place instead.
    if (isLoading && data.length === 0) {
        return <Skeleton variant="rectangular" height={500} sx={{ mt: 2 }} />
    }

    // Only before the first search. A search that returns nothing keeps the
    // grid — its toolbar is where the text search and the quality filters now
    // live, and swapping it for a placeholder left no way to undo the filter
    // that emptied the table.
    if (!hasSearched.value && data.length === 0) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 8,
                color: '#9ca3af',
                backgroundColor: '#fff',
                borderRadius: 1,
                border: '1px solid #e5e7eb'
            }}>
                <IconSeeker icon="magnifying-glass" sx={{ fontSize: 48, mb: 2 }} />
                <Typography variant="body1">
                    Pick a customer, then Search to see what the scraper last brought back
                </Typography>
            </Box>
        )
    }

    return <Box
        ref={wrapperRef}
        // scroll events do not bubble; the capture phase is the only way a
        // parent hears the grid's own scroller move.
        onScrollCapture={(event) => {
            const node = event.target
            if (node.scrollWidth > node.clientWidth) scrollLeftRef.current = node.scrollLeft
        }}
        sx={{
            position: 'relative',
            // TableSeeker wraps whatever customToolbar returns in a block-level
            // flex Box, so the funnel took a line of its own under the icons —
            // and with the search open, that line cut the field in two.
            //
            // The grid's own icons are inline boxes in a `text-align: right`
            // block, each with an 8px right margin (`searchIcon` in its
            // styles). Copying that exactly is what finally lined the funnel up
            // with them and kept it off the card's edge; overriding the row to
            // flex instead moved it around but never matched.
            //
            // `MUIDataTableToolbar-customActions` is a literal class the
            // wrapper sets, so unlike the grid's own it survives a production
            // build.
            // Inline flow cannot line it up: the grid's icons are inline-flex
            // boxes whose synthesised baseline is their bottom edge, and our
            // Badge-wrapped one lands ~8px lower however it is nudged. So the
            // row that holds them all is turned into a centred flex row.
            //
            // `:has()` targets that row through our own wrapper class, which is
            // a literal string — the grid's own classes are hashed in a
            // production build and matched nothing. The `:last-of-type` rule is
            // the same thing without `:has()`, kept as a fallback.
            '& div:has(> .MUIDataTableToolbar-customActions), & .MuiToolbar-root:not(.MuiTablePagination-toolbar) > div:last-of-type': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                flexWrap: 'nowrap',
            },
            // And TableSeeker styles that wrapper as a SECOND row under the
            // icons: `padding-top: 20px` plus a `border-top` divider. That was
            // the 10px drop and the cut line across the toolbar. Its selector
            // doubles the class to raise specificity, so plain properties lost
            // — hence `!important` on exactly those two.
            '& .MUIDataTableToolbar-customActions': {
                display: 'inline-flex',
                alignItems: 'center',
                flexShrink: 0,
                paddingTop: '0 !important',
                borderTop: 'none !important',
            },
        }}
    >
    {isLoading && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 3, height: 3 }} />}
    <Box sx={{
        opacity: isLoading ? 0.45 : 1,
        pointerEvents: isLoading ? 'none' : 'auto',
        transition: 'opacity 120ms',
    }}>
    <TableSeeker
        // Where the population lives now that there is no coverage strip: the
        // column percentages are over these rows, and saying so once here beats
        // repeating it on every header.
        title={`Raw products — ${coverageTotal.value.toLocaleString('en-US')} seen in ${windowLabel(appliedFilters.value)}`}
        data={tableRows}
        columns={columns}
        options={{
            // The grid's own column filter is off: it only sees the loaded
            // page, so it would quietly filter 50 rows out of 48.666 and look
            // like it filtered everything. The Quality filters button in the
            // filter bar does the same job against the backend.
            filter: false,
            // The grid's own magnifier, which with `serverSide` does not filter
            // anything itself: it hands the text to onSearchChange and we
            // re-query. Enter searches, Escape closes and clears — the same
            // behaviour, and the same look, as the DIPI app's.
            search: true,
            onSearchChange: (text) => { searchText.value = text || '' },
            // Sorting is server-side: the grid could only order the 50 rows on
            // screen, and the reason to sort at all is to find the extremes
            // across the whole result. It costs a filesort — 1,5 s over the
            // 48.666 rows of a 30-day window, against 45 ms for the index
            // order — which is why the backend keeps a whitelist of columns.
            sort: true,
            sortOrder: sortOrder.value
                ? { name: sortOrder.value.column, direction: sortOrder.value.direction }
                : {},
            onColumnSortChange: (column, direction) => {
                sortOrder.value = { column, direction }
            },
            print: false,
            download: false,
            // The quality filters sit with the grid's own icons, last, where
            // the DIPI app puts its funnel.
            customToolbar: () => <ScrapingDataReviewQualityFilters />,
            // The one toolbar control that earns its place: it is the list of
            // every field with its coverage, which is the question this screen
            // exists to answer.
            viewColumns: true,
            selectableRows: 'none',
            responsive: 'standard',
            serverSide: true,
            count: total,
            page: currentPage.value,
            rowsPerPage: pageSize.value,
            rowsPerPageOptions: [25, 50, 100],
            onChangePage: (page) => currentPage.value = page,
            onChangeRowsPerPage: (size) => { pageSize.value = size; currentPage.value = 0 },
            // Remember what was shown or hidden. The grid would forget on the
            // next response, because the columns are rebuilt to carry the new
            // coverage percentages.
            onViewColumnsChange: (changedColumn, action) => {
                columnDisplay.value = {
                    ...columnDisplay.value,
                    [changedColumn]: action === 'add',
                }
            },
            setRowProps: () => ({ style: { verticalAlign: 'top' } }),
        }}
        textLabels={{
            body: { noMatch: 'No products match these filters in the selected window.' }
        }}
    />
    </Box>
    </Box>
}
